import { PasswordConfiguration, PasswordConfigurationRole, PasswordOptions } from 'Global';

const passwordConfigurationRoles: PasswordConfigurationRole[] = ['superAdministrators', 'admin', 'manager', 'staff'];

function isPasswordConfigurationRole(role: string): role is PasswordConfigurationRole {
	return (passwordConfigurationRoles as string[]).includes(role);
}

/**
 * Resolves the password rules that apply to a role, falling back to the top level rules when the role
 * has no override.
 *
 * Roles reach the client as dynamic camelCased group names, so the narrowing to the closed set of
 * override keys happens here rather than being cast at each call site.
 */
export function getPasswordOptionsForRole(
	configuration: PasswordConfiguration,
	role: string,
): PasswordOptions | PasswordConfiguration {
	if (isPasswordConfigurationRole(role)) {
		return configuration[role] ?? configuration;
	}
	return configuration;
}

export function validatePassword(options: PasswordOptions, password: string): string | null {
	if (!password || password.trim().length === 0) {
		return 'Password cannot be empty.';
	}

	const {
		requiredLength = 6,
		requiredUniqueChars = 1,
		requireNonAlphanumeric = false,
		requireLowercase = false,
		requireUppercase = false,
		requireDigit = false,
	} = options;

	if (password.length < requiredLength) {
		return `Password must be at least ${requiredLength} characters long.`;
	}

	if (requireNonAlphanumeric && /^[A-Za-z0-9]*$/.test(password)) {
		return 'Password must contain at least one non-alphanumeric character.';
	}

	if (requireDigit && !/[0-9]/.test(password)) {
		return 'Password must contain at least one digit.';
	}

	if (requireLowercase && !/[a-z]/.test(password)) {
		return 'Password must contain at least one lowercase letter.';
	}

	if (requireUppercase && !/[A-Z]/.test(password)) {
		return 'Password must contain at least one uppercase letter.';
	}

	const uniqueChars = new Set(password).size;
	if (requiredUniqueChars >= 1 && uniqueChars < requiredUniqueChars) {
		return `Password must contain at least ${requiredUniqueChars} unique characters.`;
	}

	return null;
}
